import React, { createContext, useState, useContext, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const ConfirmContext = createContext(null);


export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState({
    title: '',
    content: '',
    okText: 'Continue',
    cancelText: 'Cancel',
    okButtonProps: {},
    onOk: null,
    onCancel: null,
  });

  const confirm = useCallback((opts) => {
    setOptions({
      title: opts.title || '',
      content: opts.content || '',
      okText: opts.okText || 'Continue',
      cancelText: opts.cancelText || 'Cancel',
      okButtonProps: opts.okButtonProps || {},
      onOk: opts.onOk || null,
      onCancel: opts.onCancel || null,
    });
    setOpen(true);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOk = async () => {
    handleClose();
    if (options.onOk) {
      // Pass close function as in your original example
      await options.onOk(() => setOpen(false));
    }
  };

  const handleCancel = () => {
    handleClose();
    if (options.onCancel) {
      options.onCancel();
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Dialog
        className='confirm-dialogCstm'
        open={open}
        onClose={handleCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-content"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            marginTop: '50px', // adjust this value as needed
            alignSelf: 'flex-start',
          },
        }}
      >
        <DialogTitle id="confirm-dialog-title">{options.title}</DialogTitle>
        <DialogContent dividers>
          {typeof options.content === 'string' ? <p>{options.content}</p> : options.content}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} color="primary" variant="outlined">
            {options.cancelText}
          </Button>
          <Button
            onClick={handleOk}
            variant="contained"
            sx={{
              backgroundColor: '#012B70',
              borderColor: '#012B70',
              color: 'white',
              '&:hover': {
                backgroundColor: '#011F4B',
                borderColor: '#011F4B',
              },
            }}
            {...options.okButtonProps}
            autoFocus
          >
            {options.okText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/**
 * Hook to get confirm function that activates the modal.
 * Usage:
 *   const confirm = useConfirm();
 *   confirm({...options});
 */
export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context.confirm;
}

